import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  SignalIcon, 
  MapPinIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ClockIcon,
  WifiIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface Tower {
  id: string;
  name: string;
  location: {
    lat: number;
    lng: number;
  } | null;
  status: 'online' | 'offline' | 'degraded';
  aps: Array<{
    id: string;
    name: string;
    status: string;
    frequency: number | null;
  }>;
  clients: Array<any>;
  lastUpdated?: string;
}

const TowerStatus: React.FC = () => {
  const [towers, setTowers] = useState<Tower[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [error, setError] = useState<string | null>(null);

  const fetchTowerStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch from PHP API endpoint (works on cPanel)
      const response = await fetch('/api/tower-status.php');
      
      if (!response.ok) {
        throw new Error('Failed to fetch tower status');
      }
      
      const data = await response.json();
      
      // Process tower data
      const processedTowers = data.towers.map((tower: any) => {
        const activeAPs = tower.aps.filter((ap: any) => ap.status === 'active').length;
        const totalAPs = tower.aps.length;
        
        let status: 'online' | 'offline' | 'degraded' = 'online';
        if (activeAPs === 0) {
          status = 'offline';
        } else if (activeAPs < totalAPs) {
          status = 'degraded';
        }
        
        return {
          ...tower,
          status
        };
      });
      
      setTowers(processedTowers);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching tower status:', err);
      setError('Unable to load tower status. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTowerStatus();
    
    // Auto-refresh every 2 minutes
    const interval = setInterval(fetchTowerStatus, 120000);
    
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'degraded':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'offline':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
        return <CheckCircleIcon className="h-6 w-6 text-green-600" />;
      case 'degraded':
        return <ClockIcon className="h-6 w-6 text-yellow-600" />;
      case 'offline':
        return <XCircleIcon className="h-6 w-6 text-red-600" />;
      default:
        return <SignalIcon className="h-6 w-6 text-gray-600" />;
    }
  };

  const totalTowers = towers.length;
  const onlineTowers = towers.filter(t => t.status === 'online').length;
  const degradedTowers = towers.filter(t => t.status === 'degraded').length;
  const offlineTowers = towers.filter(t => t.status === 'offline').length;
  
  // Calculate AP statistics
  const totalAPs = towers.reduce((sum, tower) => sum + tower.aps.length, 0);
  const activeAPs = towers.reduce((sum, tower) => sum + tower.aps.filter(ap => ap.status === 'active').length, 0);
  const inactiveAPs = totalAPs - activeAPs;
  
  // Calculate network health score (0-100)
  const networkScore = totalTowers > 0 
    ? Math.round(((onlineTowers * 100) + (degradedTowers * 50)) / totalTowers)
    : 0;
  
  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };
  
  const getScoreBgColor = (score: number) => {
    if (score >= 90) return 'bg-green-50 border-green-200';
    if (score >= 70) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="relative py-12 bg-gradient-to-br from-airmax-blue to-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <SignalIcon className="h-16 w-16 text-white mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Network Tower Status
            </h1>
            <p className="text-xl text-gray-100 max-w-3xl mx-auto">
              Real-time status of our network infrastructure across Golden Valley and Kingman, Arizona
            </p>
          </motion.div>
        </div>
      </section>

      {/* Unified Network Dashboard */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden"
          >
            <div className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                {/* Left Column - Network Health & Tower Stats */}
                <div className="space-y-6">
                  {/* Network Health Score */}
                  <div className="flex flex-col items-center">
                    <div className="text-xs font-semibold text-gray-600 mb-3">Network Health</div>
                    <div className="relative inline-flex items-center justify-center">
                      <svg className="transform -rotate-90" width="100" height="100">
                        <circle cx="50" cy="50" r="42" stroke="#e5e7eb" strokeWidth="8" fill="none" />
                        <motion.circle
                          cx="50" cy="50" r="42"
                          stroke={networkScore >= 90 ? '#10b981' : networkScore >= 70 ? '#eab308' : '#ef4444'}
                          strokeWidth="8" fill="none" strokeLinecap="round"
                          initial={{ strokeDashoffset: 264 }}
                          animate={{ strokeDashoffset: 264 - (264 * networkScore) / 100 }}
                          transition={{ duration: 1.5, ease: "easeOut" }}
                          strokeDasharray="264"
                        />
                      </svg>
                      <div className="absolute">
                        <div className={`text-2xl font-bold ${getScoreColor(networkScore)}`}>
                          {networkScore}<span className="text-sm">%</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      {networkScore >= 90 ? 'Excellent' : networkScore >= 70 ? 'Good' : 'Needs Attention'}
                    </div>
                  </div>

                  {/* AP Statistics */}
                  <div className="pt-6 border-t border-gray-100">
                    <div className="text-xs font-semibold text-gray-600 mb-3">Access Point Statistics</div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-600">Total APs</span>
                        <span className="text-lg font-bold text-gray-900">{totalAPs}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-600">Active</span>
                        <span className="text-lg font-bold text-green-600">{activeAPs}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-600">Inactive</span>
                        <span className="text-lg font-bold text-red-600">{inactiveAPs}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Live Network Wave Visualization */}
                <div className="lg:col-span-3">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-xs font-semibold text-gray-600">Live Network Activity</div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <ClockIcon className="h-3 w-3" />
                      <span>Updated: {lastRefresh.toLocaleTimeString()}</span>
                    </div>
                  </div>
                  
                  {/* Animated wave visualization */}
                  <div className="relative h-48 bg-white rounded-lg border border-gray-200 overflow-hidden">
                    {/* Animated flowing wave */}
                    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1000 300" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="waveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.2" />
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.05" />
                        </linearGradient>
                      </defs>
                      
                      {/* Static background wave - constant */}
                      <path
                        d="M0,150 Q250,100 500,150 T1000,150 L1000,300 L0,300 Z"
                        fill="#3b82f6"
                        fillOpacity="0.08"
                      />
                      
                      {/* Animated wave - moves up and down slowly */}
                      <motion.path
                        d="M0,150 Q250,100 500,150 T1000,150 L1000,300 L0,300 Z"
                        fill="url(#waveGradient)"
                        animate={{
                          d: [
                            "M0,150 Q250,100 500,150 T1000,150 L1000,300 L0,300 Z",
                            "M0,130 Q250,80 500,130 T1000,130 L1000,300 L0,300 Z",
                            "M0,150 Q250,100 500,150 T1000,150 L1000,300 L0,300 Z"
                          ]
                        }}
                        transition={{
                          duration: 4,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      />
                    </svg>
                    
                    {/* Data packets moving across */}
                    <div className="absolute inset-0">
                      {Array.from({ length: 8 }).map((_, i) => (
                        <motion.div
                          key={`packet-${i}`}
                          className="absolute"
                          style={{ top: `${30 + (i % 3) * 15}%` }}
                          initial={{ left: '-5%', opacity: 0 }}
                          animate={{ 
                            left: '105%',
                            opacity: [0, 1, 1, 0]
                          }}
                          transition={{
                            duration: 4 + (i * 0.5),
                            repeat: Infinity,
                            delay: i * 0.8,
                            ease: "linear"
                          }}
                        >
                          <div className="w-2 h-2 bg-blue-400 rounded-full shadow-md" />
                        </motion.div>
                      ))}
                    </div>
                    
                    {/* Outage indicators at bottom of wave */}
                    <div className="absolute inset-x-0 bottom-0 px-4 pb-2">
                      <div className="relative h-4">
                        {Array.from({ length: 24 }).map((_, i) => {
                          // Check for tower outages OR AP outages
                          const hasTowerOutage = towers.some(tower => tower.status === 'offline' && i >= 20);
                          const hasAPOutage = towers.some(tower => 
                            tower.aps.some(ap => ap.status !== 'active') && i >= 20
                          );
                          const hasOutage = hasTowerOutage || hasAPOutage;
                          const position = (i / 23) * 100;
                          
                          return hasOutage ? (
                            <motion.div
                              key={i}
                              className="absolute bottom-0"
                              style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
                              initial={{ scale: 0, opacity: 0 }}
                              animate={{ 
                                scale: [1, 1.2, 1],
                                opacity: [0.8, 1, 0.8]
                              }}
                              transition={{
                                duration: 2,
                                repeat: Infinity,
                                delay: i * 0.1
                              }}
                            >
                              <div className="w-3 h-3 bg-red-500 rounded-full shadow-lg border-2 border-white" />
                            </motion.div>
                          ) : null;
                        })}
                      </div>
                    </div>
                    
                    {/* Network status overlay */}
                    <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                        <span className="text-xs font-medium text-gray-700">Network Active</span>
                      </div>
                    </div>
                    
                    {/* Uptime percentage overlay */}
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm">
                      <div className="text-xs text-gray-500">Uptime</div>
                      <div className="text-lg font-bold text-blue-600">{networkScore}%</div>
                    </div>
                  </div>
                  
                  {/* Timeline */}
                  <div className="flex justify-between mt-2 text-xs text-gray-400">
                    <span>24h ago</span>
                    <span className="font-medium text-gray-600">Now</span>
                  </div>
                  
                  {/* Automatic Status Message */}
                  <div className="mt-4">
                    {(() => {
                      // Detect if there are any tower or AP outages in the last 24 hours
                      const hasAnyOutage = Array.from({ length: 24 }).some((_, i) => {
                        const hasTowerOutage = towers.some(tower => tower.status === 'offline' && i >= 20);
                        const hasAPOutage = towers.some(tower => 
                          tower.aps.some(ap => ap.status !== 'active') && i >= 20
                        );
                        return hasTowerOutage || hasAPOutage;
                      });
                      
                      if (!hasAnyOutage && networkScore === 100 && inactiveAPs === 0) {
                        return (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg"
                          >
                            <div className="flex-shrink-0">
                              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-green-800">All Systems Operational</p>
                              <p className="text-xs text-green-600 mt-0.5">Network is running at 100% uptime with no incidents in the last 24 hours.</p>
                            </div>
                          </motion.div>
                        );
                      } else {
                        const outageCount = Array.from({ length: 24 }).filter((_, i) => {
                          const hasTowerOutage = towers.some(tower => tower.status === 'offline' && i >= 20);
                          const hasAPOutage = towers.some(tower => 
                            tower.aps.some(ap => ap.status !== 'active') && i >= 20
                          );
                          return hasTowerOutage || hasAPOutage;
                        }).length;
                        
                        return (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg"
                          >
                            <div className="flex-shrink-0">
                              <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-red-800">Network Issues Detected</p>
                              <p className="text-xs text-red-600 mt-0.5">
                                {outageCount} incident{outageCount > 1 ? 's' : ''} detected in the last 24 hours. 
                                {inactiveAPs > 0 && ` ${inactiveAPs} AP${inactiveAPs > 1 ? 's' : ''} inactive.`} Current uptime: {networkScore}%
                              </p>
                            </div>
                          </motion.div>
                        );
                      }
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tower List */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading && towers.length === 0 ? (
            <div className="text-center py-12">
              <ArrowPathIcon className="h-12 w-12 text-airmax-blue mx-auto mb-4 animate-spin" />
              <p className="text-gray-600">Loading tower status...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <XCircleIcon className="h-12 w-12 text-red-600 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={fetchTowerStatus}
                className="px-6 py-3 bg-airmax-blue text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : (
            <>
              {/* Tower Statistics Row */}
              <div className="mb-8 bg-white rounded-lg shadow-md border border-gray-200 p-4">
                <div className="grid grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="text-xs text-gray-600 mb-1">Total Towers</div>
                    <div className="text-xl font-bold text-gray-900">{totalTowers}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 mb-1">Online</div>
                    <div className="text-xl font-bold text-green-600">{onlineTowers}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 mb-1">Offline</div>
                    <div className="text-xl font-bold text-red-600">{offlineTowers}</div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {towers.map((tower, index) => (
                <motion.div
                  key={tower.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
                >
                  <div className="p-6">
                    {/* Tower Name */}
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">
                      {tower.name}
                    </h3>
                    
                    {/* Status */}
                    <div className="flex items-center gap-2 mb-6">
                      <div className={`w-3 h-3 rounded-full ${
                        tower.status === 'online' ? 'bg-green-500' :
                        tower.status === 'degraded' ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}></div>
                      <span className={`text-lg font-medium ${
                        tower.status === 'online' ? 'text-green-700' :
                        tower.status === 'degraded' ? 'text-yellow-700' :
                        'text-red-700'
                      }`}>
                        {tower.status.charAt(0).toUpperCase() + tower.status.slice(1)}
                      </span>
                    </div>
                    
                    {/* Network Activity Visualization */}
                    <div className="mb-6 relative h-16 bg-gray-50 rounded-lg overflow-hidden">
                      {/* Background grid */}
                      <div className="absolute inset-0 opacity-20">
                        <svg className="w-full h-full">
                          <defs>
                            <pattern id={`grid-${tower.id}`} width="20" height="20" patternUnits="userSpaceOnUse">
                              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-gray-400"/>
                            </pattern>
                          </defs>
                          <rect width="100%" height="100%" fill={`url(#grid-${tower.id})`} />
                        </svg>
                      </div>
                      
                      {/* Animated network line */}
                      {tower.status === 'online' && (
                        <svg className="absolute inset-0 w-full h-full">
                          <motion.path
                            d="M 0 32 Q 50 20, 100 32 T 200 32 T 300 32 T 400 32"
                            fill="none"
                            stroke="#10b981"
                            strokeWidth="2"
                            initial={{ pathLength: 0, opacity: 0 }}
                            animate={{ 
                              pathLength: 1, 
                              opacity: [0.3, 0.8, 0.3],
                              strokeDashoffset: [0, -100]
                            }}
                            transition={{ 
                              pathLength: { duration: 2 + (index * 0.15), ease: "easeInOut", delay: index * 0.2 },
                              opacity: { duration: 2 + (index * 0.15), repeat: Infinity, ease: "easeInOut", delay: index * 0.2 },
                              strokeDashoffset: { duration: 3 + (index * 0.15), repeat: Infinity, ease: "linear", delay: index * 0.2 }
                            }}
                            strokeDasharray="10 5"
                          />
                          {/* Data packets traveling across */}
                          <motion.circle
                            cx="0"
                            cy="32"
                            r="3"
                            fill="#10b981"
                            initial={{ cx: 0 }}
                            animate={{ cx: 400 }}
                            transition={{ duration: 2.5 + (index * 0.2), repeat: Infinity, ease: "linear", delay: index * 0.25 }}
                          />
                          <motion.circle
                            cx="0"
                            cy="32"
                            r="2.5"
                            fill="#34d399"
                            initial={{ cx: 0 }}
                            animate={{ cx: 400 }}
                            transition={{ duration: 3 + (index * 0.25), repeat: Infinity, ease: "linear", delay: (index * 0.25) + 0.5 }}
                          />
                          <motion.circle
                            cx="0"
                            cy="32"
                            r="3"
                            fill="#10b981"
                            initial={{ cx: 0 }}
                            animate={{ cx: 400 }}
                            transition={{ duration: 2.8 + (index * 0.18), repeat: Infinity, ease: "linear", delay: (index * 0.25) + 1 }}
                          />
                          <motion.circle
                            cx="0"
                            cy="32"
                            r="2"
                            fill="#34d399"
                            initial={{ cx: 0 }}
                            animate={{ cx: 400 }}
                            transition={{ duration: 3.2 + (index * 0.15), repeat: Infinity, ease: "linear", delay: (index * 0.25) + 1.5 }}
                          />
                          <motion.circle
                            cx="0"
                            cy="32"
                            r="2.5"
                            fill="#10b981"
                            initial={{ cx: 0 }}
                            animate={{ cx: 400 }}
                            transition={{ duration: 2.6 + (index * 0.22), repeat: Infinity, ease: "linear", delay: (index * 0.25) + 2 }}
                          />
                        </svg>
                      )}
                      
                      {tower.status === 'degraded' && (
                        <svg className="absolute inset-0 w-full h-full">
                          <motion.path
                            d="M 0 32 L 100 32 L 150 40 L 200 32 L 300 32 L 350 40 L 400 32"
                            fill="none"
                            stroke="#eab308"
                            strokeWidth="2"
                            initial={{ pathLength: 0 }}
                            animate={{ 
                              pathLength: 1,
                              opacity: [0.3, 0.6, 0.3]
                            }}
                            transition={{ 
                              pathLength: { duration: 2 },
                              opacity: { duration: 2, repeat: Infinity }
                            }}
                          />
                        </svg>
                      )}
                      
                      {tower.status === 'offline' && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs text-gray-400 font-medium">No Activity</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Access Points */}
                    <div className="pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Access Points</span>
                        <span className="text-lg font-semibold text-gray-900">
                          {tower.aps.filter(ap => ap.status === 'active').length} / {tower.aps.length}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default TowerStatus;
